/**
 * HTTP request layer
 * if auth is required return patched axios instance(with access token in headers)
 * else return clear axios instance
 */

import axios from 'axios'
import { Capacitor } from '@capacitor/core';

import { AuthService } from '@/services/auth.service';
import { reloginWithStoredCredentials } from '@/services/auth/biometricRelogin';
import { CLIENT_HEADER, clientDescriptor } from '@/services/clientInfo';

var refreshPromise = null;

// Regex pra extrair enrollId de URLs do tipo `/enrolls/<id>/...` ou
// `/Marketplace/enrolls/<id>/...`. Capturando só dígitos pra evitar match em
// rotas que terminam com `enrolls` sem id (lista de matrículas).
const ENROLL_URL_RE = /\/enrolls\/(\d+)(?:\/|$|\?)/i;

function extractEnrollId (config) {
  if (!config || !config.url) return null;
  const m = config.url.match(ENROLL_URL_RE);
  return m ? m[1] : null;
}

// Dispatch lease/touch fire-and-forget. Importação dinâmica pra evitar
// circular (store importa services que importam http.init). Carregado uma vez
// e cacheado.
let _storePromise = null;
function getStore () {
  if (!_storePromise) _storePromise = import('@/store').then(m => m.default);
  return _storePromise;
}

function tryTouchLeaseFromResponse (response) {
  const enrollId = extractEnrollId(response?.config);
  if (!enrollId) return;
  getStore().then(store => {
    store.dispatch('lease/touch', { enrollId }).catch(() => {});
  });
}

function tryRevokeLeaseFromError (error) {
  const enrollId = extractEnrollId(error?.config);
  if (!enrollId) return;
  const reason = decodeHeader(error.response?.headers?.['x-paymentrequired-message']);
  getStore().then(store => {
    store.dispatch('lease/revoke', { enrollId, reason }).catch(() => {});
  });
}

function decodeHeader (raw) {
  if (!raw) return null;
  try { return decodeURIComponent(raw); } catch { return raw; }
}

export class Http {
  constructor (status) {
    this.isAuth = status && status.auth ? status.auth : false
    this.instance = axios.create({
      baseURL: process.env.VUE_APP_EAD_API_URL
    })

    return this.init()
  }

  init () {
    // Identificação do cliente em toda chamada à nossa API, autenticada ou não.
    this.instance.interceptors.request.use(async config => {
      // Identifies the current client for the server. Ex: Browser OR App - vX.X.X
      config.headers[CLIENT_HEADER] = await clientDescriptor;
      return config;
    });

    if (this.isAuth) {
      this.instance.interceptors.response.use(
        response => {
          // Renovação implícita do lease: qualquer 2xx em URL de enroll é prova
          // de que o backend ainda aceita o acesso desse aluno àquele curso.
          // Aproveita pra estender o TTL offline sem precisar de endpoint
          // dedicado. Regex captura `/enrolls/<id>/...` (com ou sem prefixo).
          // Fire-and-forget — falha do dispatch não afeta a response.
          try { tryTouchLeaseFromResponse(response); } catch {}
          return response;
        },
        error => {
          if (!error.response) {
            return Promise.reject(error);
          }
          // 402 = backend bloqueou acesso (assinatura cancelada, expirada, etc).
          // Marca o lease daquele enroll como revogado AGORA pra que o gate de
          // playback fique consistente com o que o backend acabou de dizer.
          // Em seguida deixa o erro propagar pra que o SubscriptionBlockedModal
          // existente continue tratando a 402 normalmente.
          if (error.response.status === 402) {
            try { tryRevokeLeaseFromError(error); } catch {}
            return Promise.reject(error);
          }
          if (error.response.status !== 401) {
            return Promise.reject(error);
          }

          var tokenStatus = error.response.headers['x-token-status'];
          if (tokenStatus == 'Token bloqueado') {
            error.message = 'Acesso bloqueado por simultaneidade';
            return Promise.reject(error);
          }

          var userStatus = error.response.headers['x-blocked-message'];
          if (userStatus) {
            //error.message = decodeURIComponent(escape(userStatus));
            error.message = decodeURIComponent(userStatus);
            return Promise.reject(error);
          }

          var requestToRetry = error.config;
          if (refreshPromise == null) {
            // No nativo, suprime o redirect do refreshTokens: o relogin biométrico
            // abaixo tenta recuperar a sessão antes de qualquer ida pro login.
            refreshPromise = AuthService.refreshTokens(
              Capacitor.isNativePlatform() ? { suppressRedirect: true } : {}
            );
          }
          //tentar pegar novo token
          return refreshPromise
            .then(token => {

                return new Promise((resolve, reject) => {

                  requestToRetry.headers.authorization = `bearer ${token.data}`;

                  axios.request(requestToRetry).then(response => {
                    refreshPromise = null;
                    resolve(response);
                  }).catch(error => {
                    if (error.response && error.response.status == 401) {
                      refreshPromise = null;
                      reject(error);
                    } else {
                      refreshPromise = null;
                      resolve(error.response);
                    }
                  });
                })
            })
            .catch(async error => {
              refreshPromise = null;

              // Na app nativa, tenta biometria antes de redirecionar para login.
              // Single-flight externo: N 401 concorrentes compartilham UM relogin
              // (sem N makeLogin / N prompts / redirect prematuro).
              if (Capacitor.isNativePlatform()) {
                try {
                  const token = await reloginWithStoredCredentials();
                  if (token) {
                    requestToRetry.headers.authorization = `bearer ${token}`;
                    return axios.request(requestToRetry);
                  }
                } catch (e) {}
              }

              // Não redireciona para login se o falhar foi por falta de rede
              const isOffline = Capacitor.isNativePlatform()
                ? !require('@/store').default.state.dom.isConnected
                : !navigator.onLine;
              if (isOffline) return Promise.reject(error);

              await AuthService.clearToLogin();
              return Promise.reject(error);
            });
        });
    }

    return this.instance;
  }
}
